import { Calendar, Clock, Copy, Edit, Plus, Search, Trash2, Users } from "lucide-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useSalonStore } from "@/lib/store/salon";
import { useShiftTemplatesGraphQL } from "../hooks/use-shift-templates-graphql";
import { TemplateDialog } from "./dialogs/template-dialog";
import { TemplatePreviewDialog } from "./dialogs/template-preview-dialog";

interface ShiftTemplate {
  id: string;
  name: string;
  description: string;
  position?: string;
  scheduleType: string;
  days: number[];
  color?: string;
  icon?: string;
  timeIntervals: Array<{
    id: string;
    startTime: string;
    endTime: string;
  }>;
  breakRules: Array<{
    id: string;
    afterHours: number;
    breakType: string;
    duration: number;
    isPaid: boolean;
  }>;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const DAYS_OF_WEEK = ["", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"]; // Index 0 unused, 1-7 for Mon-Sun

const SCHEDULE_TYPE_LABELS = {
  weekly: "Weekly",
  biweekly_even: "Bi-weekly (Even)",
  biweekly_odd: "Bi-weekly (Odd)",
};

export function ShiftTemplateTab() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState<ShiftTemplate | undefined>(undefined);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isPreviewDialogOpen, setIsPreviewDialogOpen] = useState(false);

  const currentFranchise = useSalonStore((state) => state.getCurrentFranchise());
  const {
    templates,
    loading,
    error,
    refetch,
    createTemplate,
    updateTemplate,
    deleteTemplate,
    duplicateTemplate,
  } = useShiftTemplatesGraphQL(currentFranchise?.id || "");

  // Filter templates based on search query
  const filteredTemplates =
    templates?.filter(
      (template) =>
        template.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.position?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        template.description?.toLowerCase().includes(searchQuery.toLowerCase())
    ) || [];

  const handleCreateTemplate = () => {
    setSelectedTemplate(undefined);
    setIsCreateDialogOpen(true);
  };

  const handleEditTemplate = (template: ShiftTemplate) => {
    setSelectedTemplate(template);
    setIsCreateDialogOpen(true);
  };

  const handlePreviewTemplate = (template: ShiftTemplate) => {
    setSelectedTemplate(template);
    setIsPreviewDialogOpen(true);
  };

  const handleDuplicateTemplate = async (template: ShiftTemplate) => {
    try {
      await duplicateTemplate.execute(template.id, `${template.name} (Copy)`);
    } catch (error) {
      console.error("Failed to duplicate template:", error);
    }
  };

  const handleDeleteTemplate = async (template: ShiftTemplate) => {
    if (confirm(`Are you sure you want to delete "${template.name}"?`)) {
      try {
        await deleteTemplate.execute(template.id);
      } catch (error) {
        console.error("Failed to delete template:", error);
      }
    }
  };

  const calculateTemplateHours = (intervals: ShiftTemplate["timeIntervals"]): number => {
    return intervals.reduce((total, interval) => {
      const start = new Date(`2000-01-01T${interval.startTime}:00`);
      const end = new Date(`2000-01-01T${interval.endTime}:00`);
      const diff = end.getTime() - start.getTime();
      return total + diff / (1000 * 60 * 60);
    }, 0);
  };

  const calculateBreakTime = (breakRules: ShiftTemplate["breakRules"]): number => {
    return breakRules.reduce((total, rule) => total + rule.duration, 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
          <p className="text-muted-foreground">Loading shift templates...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-destructive mb-4">Failed to load shift templates</p>
          <Button onClick={() => refetch()} variant="outline">
            Try Again
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4 flex-1">
          <div className="relative max-w-sm">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
            <Input
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
          {searchQuery && (
            <p className="text-sm text-muted-foreground">
              {filteredTemplates.length} of {templates?.length || 0} templates
            </p>
          )}
        </div>
        <Button onClick={handleCreateTemplate}>
          <Plus className="w-4 h-4 mr-2" />
          Create Template
        </Button>
      </div>

      {/* Templates List */}
      {filteredTemplates.length === 0 ? (
        <div className="text-center py-12">
          {searchQuery ? (
            <>
              <Search className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No templates found</h3>
              <p className="text-muted-foreground mb-4">
                Try adjusting your search terms or create a new template.
              </p>
              <Button onClick={handleCreateTemplate} variant="outline">
                <Plus className="w-4 h-4 mr-2" />
                Create Template
              </Button>
            </>
          ) : (
            <>
              <Clock className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">No shift templates yet</h3>
              <p className="text-muted-foreground mb-4">
                Create your first shift template to get started with the four-layer system.
              </p>
              <Button onClick={handleCreateTemplate}>
                <Plus className="w-4 h-4 mr-2" />
                Create First Template
              </Button>
            </>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {filteredTemplates.map((template) => (
            <Card
              key={template.id}
              className="group hover:shadow-md transition-all duration-200 hover:border-primary/20"
            >
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  {/* Header Section */}
                  <div className="flex items-start gap-4 flex-1">
                    <div
                      className="w-4 h-4 rounded-full mt-1 flex-shrink-0"
                      style={{ backgroundColor: template.color || "#3b82f6" }}
                    />
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-semibold text-lg">{template.name}</h3>
                        {template.position && (
                          <Badge variant="secondary" className="text-xs">
                            {template.position}
                          </Badge>
                        )}
                      </div>
                      {template.description && (
                        <p className="text-sm text-muted-foreground mb-2">{template.description}</p>
                      )}
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span>
                          {SCHEDULE_TYPE_LABELS[
                            template.scheduleType as keyof typeof SCHEDULE_TYPE_LABELS
                          ] || template.scheduleType}
                        </span>
                        <span>•</span>
                        <span>
                          {calculateTemplateHours(template.timeIntervals).toFixed(1)}h total
                        </span>
                        <span>•</span>
                        <span>{calculateBreakTime(template.breakRules)}min breaks</span>
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handlePreviewTemplate(template as ShiftTemplate)}
                      className="h-8 w-8 p-0"
                      title="Preview template"
                    >
                      <Calendar className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleEditTemplate(template as ShiftTemplate)}
                      className="h-8 w-8 p-0"
                      title="Edit template"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDuplicateTemplate(template as ShiftTemplate)}
                      className="h-8 w-8 p-0"
                      title="Duplicate template"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDeleteTemplate(template as ShiftTemplate)}
                      className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                      title="Delete template"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Content Section */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Days of Week */}
                  <div>
                    <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      Schedule
                    </h4>
                    <div className="flex items-center gap-1 flex-wrap">
                      {DAYS_OF_WEEK.map((day, index) => {
                        if (index === 0 || !day) return null; // Skip index 0 (unused)
                        return (
                          <div
                            key={day}
                            className={`text-xs px-2 py-1 rounded-md ${
                              template.days.includes(index)
                                ? "bg-primary text-primary-foreground"
                                : "bg-muted text-muted-foreground"
                            }`}
                          >
                            {day}
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Time Intervals */}
                  <div>
                    <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      Time Intervals
                    </h4>
                    <div className="space-y-1">
                      {template.timeIntervals.slice(0, 2).map((interval) => (
                        <div key={interval.id} className="text-sm">
                          {interval.startTime} - {interval.endTime}
                        </div>
                      ))}
                      {template.timeIntervals.length > 2 && (
                        <div className="text-xs text-muted-foreground">
                          +{template.timeIntervals.length - 2} more intervals
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Break Rules */}
                  <div>
                    <h4 className="text-sm font-medium mb-2 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Break Rules
                    </h4>
                    <div className="space-y-1">
                      {template.breakRules.length === 0 ? (
                        <div className="text-sm text-muted-foreground">No break rules</div>
                      ) : (
                        <>
                          {template.breakRules.slice(0, 2).map((rule, index) => (
                            <div key={index} className="text-sm">
                              {rule.duration}min {rule.breakType.toLowerCase()} after{" "}
                              {rule.afterHours}h
                            </div>
                          ))}
                          {template.breakRules.length > 2 && (
                            <div className="text-xs text-muted-foreground">
                              +{template.breakRules.length - 2} more rules
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialogs */}
      <TemplateDialog
        isOpen={isCreateDialogOpen}
        onClose={() => {
          setIsCreateDialogOpen(false);
          setSelectedTemplate(undefined);
        }}
        template={selectedTemplate as never}
        onSave={async (templateData) => {
          try {
            if (selectedTemplate) {
              await updateTemplate.execute(selectedTemplate.id, templateData);
            } else {
              await createTemplate.execute(templateData);
            }
            setIsCreateDialogOpen(false);
            setSelectedTemplate(undefined);
          } catch (error) {
            console.error("Failed to save template:", error);
          }
        }}
      />

      <TemplatePreviewDialog
        isOpen={isPreviewDialogOpen}
        onClose={() => {
          setIsPreviewDialogOpen(false);
          setSelectedTemplate(undefined);
        }}
        template={selectedTemplate ?? null}
      />
    </div>
  );
}
